import { mapGetters, mapActions, mapMutations } from 'vuex'

export default {
  name: 'GoogleAnalyticsIntegration',
  data () {
    return {
      isLoading: {
        integrations: false,
        save: false,
        delete: false
      },
      settings: [
        { label: 'Property (required)', id: 'ga_property', value: '', options: [], disabled: false, required: true }
      ],
      permissionError: false,
      errorMessage: ''
    }
  },
  computed: {
    ...mapGetters([
      'integrations',
      'googleAnalyticsIntegration',
      'searchConsoleIntegration'
    ]),
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    hasIntegration () {
      return !!this.googleAnalyticsIntegration?.ga_property
    }
  },
  methods: {
    ...mapActions([
      'getGoogleAnalyticsProperties',
      'saveGoogleAnalyticsProperty',
      'getIntegrations',
      'deleteIntegration'
    ]),
    ...mapMutations([
      'setAuthToken',
      'setSnackbar'
    ]),
    mapGoogleAnalyticsProperties (data = []) {
      return data.map(item => {
        const property = item.property.split('/')?.[1] ?? -1
        return { name: item.displayName, value: Number(property) }
      })
    },
    async handleSave () {
      this.isLoading.save = true
      try {
        await this.saveGoogleAnalyticsProperty({
          contentplanId: this.contentplanId,
          gaProperty: this.settings[0].value,
          gaName: this.settings[0].options.find(item => item.value === this.settings[0].value)?.name,
          type: 'ga-4'
        })
        this.$router.push({
          name: 'ContentplanSettings',
          params: { contentplanId: this.contentplanId }
        })
      } catch (error) {
        console.error(error)
      }
      this.isLoading.save = false
    },
    async loadGoogleAnalyticsProperties () {
      try {
        const { properties } = await this.getGoogleAnalyticsProperties()
        this.settings[0].options = this.mapGoogleAnalyticsProperties(properties)
        this.permissionError = false
        this.errorMessage = ''
      } catch (error) {
        console.error('Error loading GA properties:', error)
        const statusCode = error?.response?.status
        const errorMsg = (error?.response?.data?.message || error.message || '').toLowerCase()
        console.log('Status code:', statusCode)
        console.log('Error message:', errorMsg)

        const isAuthError = statusCode === 401 || statusCode === 403 || errorMsg.includes('token') || errorMsg.includes('auth') || errorMsg.includes('permission') || errorMsg.includes('access denied') || errorMsg.includes('unauthorized') || errorMsg.includes('forbidden') || errorMsg.includes('not found')
        console.log('Is auth error:', isAuthError)

        if (isAuthError) {
          this.permissionError = true
          this.errorMessage = 'You have not allowed access to Google Analytics. Please reauthenticate to grant access.'
          console.log('Permission error flag set to true')
        } else {
          this.errorMessage = errorMsg || 'Failed to load Google Analytics properties'
        }
      }
    },
    fillValues () {
      const integration = this.googleAnalyticsIntegration
      if (!integration) return
      this.settings[0].value = integration.ga_property ?? ''
    },
    handleReauthenticate () {
      const nextUrl = encodeURIComponent(window.location.href)
      const promptValue = encodeURIComponent('select_account consent')
      const params = `next=${nextUrl}&workspaceId=${this.workspaceId}&scope=all&prompt=${promptValue}`
      const authUrl = window.location.origin + '/auth/in?' + params
      console.log('Reauthenticating with URL:', authUrl)
      window.location.href = authUrl
    },
    async handleDelete () {
      if (!this.hasIntegration || this.isLoading.delete) return
      if (!window.confirm('are you sure you want to remove integration')) return

      this.isLoading.delete = true
      try {
        await this.deleteIntegration({
          contentplanId: this.contentplanId,
          integrationType: 'google-analytics'
        })
        await this.getIntegrations({ contentplanId: this.contentplanId })
        this.setSnackbar({ open: true, text: 'Integration removed.', type: 'success' })
        this.$router.push({
          name: 'ContentplanSettings',
          params: { contentplanId: this.contentplanId, workspaceId: this.workspaceId }
        })
      } catch (error) {
        this.setSnackbar({ open: true, text: `Error: ${error?.response?.data?.message || error.message || 'Could not remove integration'}`, type: 'error' })
      } finally {
        this.isLoading.delete = false
      }
    }
  },
  async mounted () {
    // Check if we received a new auth token after OAuth flow
    const token = this.$route.query.token
    if (token) {
      console.log('Received new auth token after OAuth, updating session')
      this.setAuthToken(token)
      // Remove token from URL without page reload
      this.$router.replace({
        query: { ...this.$route.query, token: undefined }
      }).catch(() => {})
    }

    this.isLoading.integrations = true
    try {
      await Promise.all([
        this.getIntegrations({ contentplanId: this.contentplanId }),
        this.loadGoogleAnalyticsProperties()
      ])
      this.fillValues()
    } catch (error) {
      console.error(error)
    }
    this.isLoading.integrations = false
  }
}
